import { KNOWN_FEATURES, levelLabelOf } from '@innermatelic/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type LicenseRow } from '../../lib/api.js';

/** feature 키 → 짧은 표기(라벨의 괄호 앞부분). */
function shortFeatureLabel(key: string): string {
  const f = KNOWN_FEATURES.find((x) => x.key === key);
  return f ? f.label.replace(/\s*\(.*$/, '') : key;
}

/** features[] → 사람이 읽는 표기. ['*'] = 전체. */
function renderFeatures(features: string[]) {
  if (features.length === 1 && features[0] === '*') return <span>전체</span>;
  return (
    <>
      {features.map((k, i) => (
        <span key={k}>
          {i > 0 ? ', ' : ''}
          <span className={k === 'studio' ? 'text-brand-600 font-medium' : ''}>
            {shortFeatureLabel(k)}
          </span>
        </span>
      ))}
    </>
  );
}

export function CustomerLicenses() {
  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ licenses: LicenseRow[] }>('/api/customer/licenses')
      .then((res) => setLicenses(res.licenses))
      .catch((err) => setError((err as Error).message));
  }, []);

  return (
    <div>
      <header className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">내 라이선스</h1>
        <Link
          to="/activate"
          className="px-3 py-1.5 bg-brand-400 hover:bg-brand-600 text-white text-sm font-medium rounded-md"
        >
          새 활성화
        </Link>
      </header>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-surface border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-medium">License Key</th>
              <th className="text-left px-4 py-2 font-medium">등급</th>
              <th className="text-left px-4 py-2 font-medium">종류</th>
              <th className="text-left px-4 py-2 font-medium">만료</th>
              <th className="text-left px-4 py-2 font-medium">활성화</th>
              <th className="text-left px-4 py-2 font-medium">기능</th>
              <th className="text-left px-4 py-2 font-medium">상태</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {licenses.map((l) => {
              const expired = l.expiresAt && new Date(l.expiresAt).getTime() < Date.now();
              return (
                <tr key={l.id} className="hover:bg-neutral-50/50">
                  <td className="px-4 py-2 font-mono text-xs">{l.licenseKey}</td>
                  <td className="px-4 py-2 text-neutral-700 text-xs font-medium">
                    {levelLabelOf(l.level) ?? l.level ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-neutral-600">
                    {l.type === 'perpetual' ? '영구' : '구독'}
                  </td>
                  <td className="px-4 py-2 text-neutral-600 text-xs">
                    {l.expiresAt ? new Date(l.expiresAt).toLocaleDateString('ko-KR') : '—'}
                  </td>
                  <td className="px-4 py-2 text-neutral-600">
                    {l.activationCount} / {l.maxActivations}
                  </td>
                  <td className="px-4 py-2 text-neutral-600 text-xs">
                    {renderFeatures(l.features)}
                  </td>
                  <td className="px-4 py-2">
                    {l.revokedAt ? (
                      <span className="text-red-700 text-xs font-medium">revoked</span>
                    ) : expired ? (
                      <span className="text-amber-700 text-xs font-medium">expired</span>
                    ) : (
                      <span className="text-green-700 text-xs font-medium">active</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {licenses.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-neutral-500">
                  발급받은 라이선스가 없습니다. 본사에 문의하세요.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
